import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DatePicker from '../../../components/Picker/DatePicker';
import TimePicker from '../../../components/Picker/TimePicker';
import OrderPayment from '../../../components/Order/OrderPayment';
import dayjs from 'dayjs';
import product from "../../../assets/img/detailproduct.png";
import { STORE_LIST_URL, MY_PAGE_EXTENSION_URL } from "../../../utils/urls";
import instance from "../../../services/services";
import { debounce } from 'lodash';
import { formatLocalString } from "../../../utils/validation";
import PaymentService from "../../../services/Payment/PaymentService";
import EquipmentRatingPopup from "../../../components/Popup/EquipmentRatingPopup";
import PopupLoginError from "../../../components/Popup/LoginError";
import { useLanguage } from "../../../hooks/useLanguage";

// 디바운스된 API 호출 함수 생성
const debouncedFn = debounce((callback) => {
  callback();
}, 300);

// formatTime 함수 추가
const formatTime = (hour, min) => {
  const formattedHour = String(hour).padStart(2, '0');
  const formattedMin = String(min).padStart(2, '0');
  return `${formattedHour}:${formattedMin}`;
};


function OrderExtension(){
  const { t } = useLanguage();
  const requestParamsRef = useRef({});
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const [isPopupLoginError, setIsPopupLoginError] = useState(false);
  
  // URL 쿼리 파라미터에서 orderNumber 추출
  const searchParams = new URLSearchParams(location.search);
  const orderNumber = searchParams.get('orderNumber') || '';
  
  // location state에서 추가 정보 추출
  const rentalStoreName = location.state?.rental_store_name || '';
  const rentalSdate = location.state?.rental_sdate || '';
  
  // rental_sdate에서 시간 부분 추출 (HH:MM:SS 형식에서 HH시 MM분 형식으로 변환)
  let initialRentalTime = '';
  if (rentalSdate) {
    const timePart = rentalSdate.split(' ')[1];
    if (timePart) {
      const [hours] = timePart.split(':');
      initialRentalTime = `${hours}시`;
    }
  }
  
  // 추출한 주문번호를 orderSeq 상태로 설정
  const orderSeq = useRef(orderNumber);
  
  const rentalSelectRef = useRef(null); 
  const returnSelectRef = useRef(null);
  const [memo, setMemo] = useState('');
  // const [extensionPop, setExtensionPop] = useState(false);
  const [storeList, setStoreList] = useState([]); // 대여 지점 리스트
  const [isShow, setIsShow] = useState({}); // 빈 객체로 초기화
  const [cartListData, setCartListData] = useState({
    bundleMain: [],
    bundleAdd: [],
    bundleAcc: []
  });
  const [orderSummary, setOrderSummary] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const selectedProductIdsRef = useRef([]);
  const [rentalState, setRentalState] = useState({
    isOpen: false,
    selectItem: rentalStoreName,
    selectValue: '',
    storeInfo: null
  });

  // 장비평점 팝업
  const [isEquipmentPopup, setEquipmentPopup ] = useState(false);
  const handleEquipmentPopup = () => {
    setEquipmentPopup(true)
  };


  // requestParams 상태 정의
  const [requestParams, setRequestParams] = useState({
    point: "",
    allPointUse: "N",
    balance: "",
    allBalanceUse: "N",
    balance_price: ""
  });

  const [returnState, setReturnState] = useState({
    isOpen: false,
    selectItem: '',
    selectValue: '',
    storeInfo: null
  });

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isChecked, setIsChecked] = useState({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  // 대여 날짜/시간 상태 추가 - location.state에서 시간 정보 포함하여 초기화
  const [rentalDateTime, setRentalDateTime] = useState({
    date: rentalSdate ? dayjs(rentalSdate.split(' ')[0]) : dayjs(),
    time: initialRentalTime
  });

  // 반납 날짜/시간 상태 추가
  const [returnDateTime, setReturnDateTime] = useState({
    date: rentalSdate ? dayjs(rentalSdate.split(' ')[0]) : dayjs(),
    time: ''
  });

  // 컴포넌트 상단에 minSelectableDate 상태 추가
  const [minSelectableDate, setMinSelectableDate] = useState(
    rentalSdate ? dayjs(rentalSdate.split(' ')[0]) : dayjs()
  );

  // 그 후 페이지가 로드될 때 한 번 설정
  useEffect(() => {
    if (rentalSdate) {
      setMinSelectableDate(dayjs(rentalSdate.split(' ')[0]));
    }
  }, [rentalSdate]);

  const setRequestParamsAndRef = (newParams) => {
    requestParamsRef.current = newParams;
    setRequestParams(newParams); // UI도 동기화
  };

  // 총 대여일수 계산을 위한 state 추가
  const [totalRentalDays, setTotalRentalDays] = useState(0);
  

  // 대여일수 계산 함수 - 먼저 정의하여 에러 해결
  const calculateRentalDays = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = dayjs(startDate).startOf('day');
    const end = dayjs(endDate).startOf('day');
    const days = end.diff(start, 'day') + 1;
    setTotalRentalDays(days);
    return days;
  }, []);

  const initializeRequestParams = useCallback(() => {
    setRequestParams(prev => ({
      ...prev,
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      pay_select_goods: selectedProductIds,
      // 초기화 시점에서 나머지 값들은 "" 또는 기본값
      additional_sale: "",
      point: "",
      allPointUse: "N",
      download_seq: "",
      deposit_price: "",
      allDepositUse: "N",
      balance: "",
      allBalanceUse: "N",
      maxCouponUse: "N",
      undecidedChk: "N"
    }));
  }, [rentalState.selectValue, returnState.selectValue, rentalDateTime, returnDateTime, selectedProductIds]);
  

  // API 호출 함수 추가
  const callExtensionAPI = useCallback((customParams = {}) => {
    const apiParams = {
      ...requestParams,
      ...customParams
    };
    
    return instance.post(MY_PAGE_EXTENSION_URL, apiParams)
      .then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          setCartListData({
            bundleMain: data.cartList?.bundleMain || [],
            bundleAdd: data.cartList?.bundleAdd || [],
            bundleAcc: data.cartList?.bundleAcc || []
          });
          setOrderSummary(data);
          return data;
        }
        return null;
      })
      .catch(error => {
        console.error('API 호출 오류:', error);
        return null;
      });
  }, [requestParams]);

  const handleUpdateParams = async (newParams) => {
    const updated = { ...requestParamsRef.current };
  
    // 항상 포인트 관련 상태 적용
    updated.point = newParams.point ?? updated.point ?? '';
    updated.allPointUse = newParams.allPointUse ?? updated.allPointUse ?? 'N';
  
    // 항상 예치금 관련 상태 적용
    updated.balance = newParams.balance ?? updated.balance ?? '';
    updated.allBalanceUse = newParams.allBalanceUse ?? updated.allBalanceUse ?? 'N';
  
    // 항상 쿠폰 관련 상태 적용
    updated.download_seq = newParams.download_seq ?? updated.download_seq ?? '';
    updated.maxCouponUse = newParams.maxCouponUse ?? updated.maxCouponUse ?? 'N';
  
    // 현금영수증
    updated.deposit_cash_receipt = newParams.deposit_cash_receipt ?? updated.deposit_cash_receipt ?? 'N';
    updated.deposit_cash_num = newParams.deposit_cash_num ?? updated.deposit_cash_num ?? '';
  
    // 최종 업데이트
    setRequestParamsAndRef(updated);
  
    // API 요청
    const apiParams = {
      ...updated,
      order_kind: "visit",
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      pay_select_goods: selectedProductIdsRef.current
    };
  
    // API 호출 생략 가능
    try {
      const response = await instance.post(MY_PAGE_EXTENSION_URL, apiParams);
      if (response.data.code === 200) {
        const data = response.data.data;
        setCartListData({
          bundleMain: data.cartList?.bundleMain || [],
          bundleAdd: data.cartList?.bundleAdd || [],
          bundleAcc: data.cartList?.bundleAcc || []
        });
        setOrderSummary(data);
      }
    } catch (error) {
      console.error('API 호출 실패:', error);
    }
  };
  

  // 페이지 초기 로드 시 MY_PAGE_EXTENSION_URL 호출
  useEffect(() => {
    const fetchStoreList = async () => {
      try {
        const response = await instance.post(STORE_LIST_URL);
        const storeListData = response.data.data;
        setStoreList(storeListData);
        
        // 지점 목록을 가져온 후 rentalStoreName으로 store_seq 찾기
        let storeSeq = "";
        if (rentalStoreName) {
          const matchedStore = storeListData.find(store => store.store_name === rentalStoreName);
          if (matchedStore) {
            storeSeq = matchedStore.store_seq;
            // rental_store_seq 값을 찾았으므로 바로 상태 업데이트
            setRentalState(prev => ({
              ...prev,
              selectItem: rentalStoreName,
              selectValue: storeSeq
            }));
          }
        }
        
        // 이제 extension 데이터 호출
        fetchExtensionData(storeSeq);
      } catch (error) {
        console.error('대여지점 리스트 가져오기 실패:', error);
        // 실패하더라도 extension 데이터는 호출
        fetchExtensionData("");
      }
    };
    
    const fetchExtensionData = async (storeSeq) => {
      try {
        const initialRentalSdate = rentalSdate || "";
        
        const params = {
          origin_order_seq: orderNumber,
          rental_store_seq: storeSeq, // 찾은 storeSeq 값 사용
          return_store_seq: "",
          rental_sdate: initialRentalSdate,
          rental_edate: "",
          additional_sale: "",
          point: "",
          allPointUse: "N",
          download_seq: "",
          deposit_price: "",
          allDepositUse: "N",
          undecidedChk: "N"
        };
        
        const response = await instance.post(MY_PAGE_EXTENSION_URL, params);
        if (response.data.code === 200) {
          const data = response.data.data;
          // 응답 데이터 구조에 맞게 상태 업데이트
          setCartListData({
            bundleMain: data.cartList?.bundleMain || [],
            bundleAdd: data.cartList?.bundleAdd || [],
            bundleAcc: data.cartList?.bundleAcc || []
          });
          setOrderSummary(data);
          
          // 기존 대여/반납 정보 설정
          if (data.order_info) {
            setRentalState(prev => ({
              ...prev,
              selectItem: rentalStoreName || data.order_info.rental_store_name || '',
              selectValue: data.order_info.rental_store_seq || '',
              storeInfo: {
                startHour: data.order_info.opening_start_hour || '00',
                startMin: data.order_info.opening_start_min || '00',
                endHour: data.order_info.opening_end_hour || '0',
                endMin: data.order_info.opening_end_min || '0',
                isFullTime: true
              }
            }));
            
            setReturnState(prev => ({
              ...prev,
              selectItem: data.order_info.return_store_name || '',
              selectValue: data.order_info.return_store_seq || '',
              storeInfo: {
                startHour: data.order_info.return_opening_start_hour || '00',
                startMin: data.order_info.return_opening_start_min || '00',
                endHour: data.order_info.return_opening_end_hour || '0',
                endMin: data.order_info.return_opening_end_min || '0',
                isFullTime: true
              }
            }));
            
            // 날짜 및 시간 설정 - 이미 state에서 설정한 값이 있으면 유지
            if (!initialRentalTime) {
              const apiRentalDate = data.order_info.rental_sdate ? dayjs(data.order_info.rental_sdate.split(' ')[0]) : dayjs();
              const apiRentalTime = data.order_info.rental_sdate ? data.order_info.rental_sdate.split(' ')[1].substring(0, 5).replace(':', '시 ') + '분' : '';
              
              setRentalDateTime({
                date: apiRentalDate,
                time: apiRentalTime
              });
            }
            
            const returnDate = data.order_info.rental_edate ? dayjs(data.order_info.rental_edate.split(' ')[0]) : dayjs();
            const returnTime = data.order_info.rental_edate ? data.order_info.rental_edate.split(' ')[1].substring(0, 5).replace(':', '시 ') + '분' : '';
            
            setReturnDateTime({
              date: returnDate,
              time: returnTime
            });
            
            // 대여일수 계산
            calculateRentalDays(rentalDateTime.date, returnDate);
          }
          
          // 상품 ID 수집
          const allIds = [
            ...(data.cartList?.bundleMain?.map(item => item.cart_option_seq) || []),
            ...(data.cartList?.bundleAdd?.map(item => item.cart_option_seq) || []),
            ...(data.cartList?.bundleAcc?.map(item => item.cart_option_seq) || [])
          ].filter(id => id && id.trim() !== ''); // 유효한 ID만 필터링
          
          setSelectedProductIds(allIds);
        }
      } catch (error) {
        console.error('연장 데이터 가져오기 실패:', error);
      }
    };
    
    if (orderNumber) {
      fetchStoreList();
    }
  }, [orderNumber, rentalStoreName, rentalSdate, initialRentalTime, calculateRentalDays, rentalDateTime.date]);

  // orderSummary가 업데이트될 때마다 requestParams 업데이트
  useEffect(() => {
    if (orderSummary && !requestParamsRef.current.origin_order_seq) {
      initializeRequestParams();
    }
  }, [orderSummary, initializeRequestParams]);
  
  // 컴포넌트 내부에 결제 관련 상태 변수 추가
  const [isPayment, setIsPayment] = useState(false);

  // 확장 요청 처리 함수를 이니시스 결제 로직으로 수정
  const handleExtension = async () => {
    // 대여 시간과 반납 시간 검증 추가
    if (!rentalDateTime.time && type === 'visit') {
      setIsPopupLoginError(t('rental_time_selection'));
      return;
    }
    
    if (!returnDateTime.time && type === 'visit') {
      setIsPopupLoginError('return_time_selection');
      return;
    }

    // 필수 약관 동의 확인
    if (!Object.values(isChecked).every(Boolean)) {
      setIsPopupLoginError(t('agree_required_terms'));
      return;
    }

    // 결제 요청 데이터 준비
    const paymentForm = {
      order_seq: orderSeq.current,                
      reorder_chk: "N",            
      order_type: type,        
      rental_store_seq: rentalState.selectValue,        
      return_store_seq: returnState.selectValue,        
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      download_seq: requestParamsRef.current.maxCouponUse === 'Y' 
        ? orderSummary?.totalInfo?.maxDiscountCouponSeq 
        : requestParamsRef.current.download_seq || "",
      point: requestParamsRef.current.point || "",                
      balance: requestParamsRef.current.balance || "",
      shipping_cost: orderSummary?.totalInfo?.delivery_price || 0,
      deposit_price: orderSummary?.totalInfo?.deposit_price || 0,
      balance_cash_receipt: requestParamsRef.current.deposit_cash_receipt || 'N',   
      balance_cash_num: requestParamsRef.current.deposit_cash_num || '',      // 현금영수증 번호
      
      depositInfo: {
        deposit_refund_account: "",
        deposit_refund_mname: "",
        deposit_refund_name: "",
        deposit_refund_price: orderSummary?.totalInfo?.deposit_price || 0,
      },
      discountInfo: {
        additional_sale: orderSummary?.totalInfo?.first_payment_sale || 0,
        mem_group_sale: orderSummary?.totalInfo?.sumGroupSale || 0,
        mem_type_sale: orderSummary?.totalInfo?.sumMtypeSale || 0
      },
      deliveryInfo: {
        recipient_name: "",
        recipient_phone: "",
        recipient_zipcode: "",
        recipient_address_street: "",
        recipient_address_detail: "",
        recipient_memo: ""
      },
      
      insuranceInfo: {
        insurance_seq: requestParamsRef.current.insurance_seq || 0,
        insurance_repair_per: orderSummary?.insuranceList?.find(
          insurance => insurance.insurance_seq === requestParamsRef.current.insurance_seq
        )?.insurance_repair_per || 0,
        insurance_price: orderSummary?.totalInfo?.insurance_price || 0
      },
      
      user_memo: memo,
      pay_select_goods: selectedProductIds.filter(id => {
        // bundleMain, bundleAdd, bundleAcc에서 체크된 상품만 필터링
        const isMainChecked = cartListData.bundleMain?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        const isAddChecked = cartListData.bundleAdd?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        const isAccChecked = cartListData.bundleAcc?.some(item => 
          item.cart_option_seq === id && item.isChecked);
        return isMainChecked || isAddChecked || isAccChecked;
      })
    };

    const result = await PaymentService.requestPayment(paymentForm);

    if (result.success) {
      const data = result.data;

      // 0원 결제 처리
      if (data.price === 0) {
        const zeroPaymentData = PaymentService.createZeroPaymentData(data);
        setIsPopupLoginError('연장 신청이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/myPage/my-order`, {
          state: {
            order_seq: zeroPaymentData.orderSeq,
            settleprice: zeroPaymentData.settleprice,
            order_kind: zeroPaymentData.orderKind,
            order_status: "free",
          }
        });
        return;
      }

      if (result.isMobile) {
        // 모바일에서는 새 창에서 결제 처리
        const paymentWindow = PaymentService.processMobilePayment(data, window.location.origin);
        if (!paymentWindow) {
          setIsPopupLoginError('결제창을 열 수 없습니다. 팝업 차단을 확인해주세요.');
        }
      } else {
        setIsPayment(true);

        setTimeout(() => {
          const iframe = document.getElementById('payment-container');
          if (iframe) {
            const paymentUrl = PaymentService.createPaymentUrl(data, window.location.origin);
            iframe.src = paymentUrl;
          } else {
            console.error('결제창 iframe을 찾을 수 없습니다.');
            setIsPayment(false);
          }
        }, 100);
      }
    } else {
      setIsPopupLoginError(result.message || '연장 신청 요청에 실패했습니다.');
      setIsPayment(false);
    }
  };

  // 컴포넌트 내부에 결제 완료 메시지 처리를 위한 useEffect 추가
  useEffect(() => {
    const handlePaymentMessage = (e) => {
      const result = PaymentService.handlePaymentMessage(e);

      if (!result) return;

      if (result.success) {
        setIsPopupLoginError('연장 신청이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/myPage/my-order`, {
          state: {
            order_seq: result.orderSeq,
            settleprice: result.settleprice,
            order_kind: result.orderKind
          }
        });
      } else {
        setIsPopupLoginError(result.message || '연장 신청이 실패하였습니다.');
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => window.removeEventListener('message', handlePaymentMessage);
  }, [navigate]);

  // 컴포넌트 내부에 모바일 결제 결과 확인을 위한 useEffect 추가
  useEffect(() => {
    const paymentResult = PaymentService.checkMobilePaymentResult();
    if (paymentResult) {
      if (paymentResult.success) {
        setIsPopupLoginError('연장 신청이 완료되었습니다.\n주문정보는 마이페이지에서 확인 가능합니다.');
        navigate(`/myPage/my-order`, {
          state: {
            order_seq: paymentResult.orderSeq,
            settleprice: paymentResult.settleprice,
            order_kind: paymentResult.orderKind
          }
        });
      } else {
        setIsPopupLoginError(paymentResult.message || '연장 신청이 실패하였습니다.');
      }
    }
  }, [navigate]);

  // selectedProductIds 변경 시 ref 업데이트
  useEffect(() => {
    selectedProductIdsRef.current = selectedProductIds;
  }, [selectedProductIds]);

  // 패키지 보기 버튼 핸들러 수정
  const handleShow = (cart_option_seq) => {
    setIsShow(prev => ({
      ...prev,
      [cart_option_seq]: !prev[cart_option_seq]
    }));
  };

  // 대여 지점 선택 핸들러
  const handleRentalSelectItem = (store) => {
    // 24시간 영업 여부 확인
    const is24Hours = store.opening_hour_set === 'N' || 
                    (store.opening_start_hour === '00' && store.opening_end_hour === '00');
    
    setRentalState({
      selectItem: store.store_name,
      selectValue: store.store_seq,
      isOpen: false,
      storeInfo: {
        startHour: store.opening_start_hour,
        startMin: store.opening_start_min,
        endHour: store.opening_end_hour,
        endMin: store.opening_end_min,
        isFullTime: is24Hours
      }
    });

    // MY_PAGE_EXTENSION_URL API 호출
    const params = {
      origin_order_seq: orderSeq.current,
      rental_store_seq: store.store_seq,
      return_store_seq: returnState.selectValue,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      additional_sale: "",
      point: "",
      allPointUse: "N",
      download_seq: "",
      deposit_price: "",
      allDepositUse: "N",
      undecidedChk: "N"
    };

    debouncedFn(() => {
      instance.post(MY_PAGE_EXTENSION_URL, params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList?.bundleMain || [],
              bundleAdd: data.cartList?.bundleAdd || [],
              bundleAcc: data.cartList?.bundleAcc || []
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('대여 지점 선택 중 오류:', error);
        });
    });
  };

  // 반납 지점 선택 핸들러
  const handleReturnSelectItem = (store) => {
    // 24시간 영업 여부 확인
    const is24Hours = store.opening_hour_set === 'N' || 
                    (store.opening_start_hour === '00' && store.opening_end_hour === '00');
    
    setReturnState({
      selectItem: store.store_name,
      selectValue: store.store_seq,
      isOpen: false,
      storeInfo: {
        startHour: store.opening_start_hour,
        startMin: store.opening_start_min,
        endHour: store.opening_end_hour,
        endMin: store.opening_end_min,
        isFullTime: is24Hours
      }
    });

    // MY_PAGE_EXTENSION_URL API 호출
    const params = {
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue,
      return_store_seq: store.store_seq,
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      additional_sale: "",
      point: "",
      allPointUse: "N",
      download_seq: "",
      deposit_price: "",
      allDepositUse: "N",
      undecidedChk: "N"
    };

    debouncedFn(() => {
      instance.post(MY_PAGE_EXTENSION_URL, params)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList?.bundleMain || [],
              bundleAdd: data.cartList?.bundleAdd || [],
              bundleAcc: data.cartList?.bundleAcc || []
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('반납 지점 선택 중 오류:', error);
        });
    });
  };

  // 대여 날짜 변경 핸들러 예시
  const handleRentalDateChange = (newDate) => {
    setRentalDateTime(prev => ({ ...prev, date: newDate }));
    calculateRentalDays(newDate, returnDateTime.date);

    const newParams = {
      rental_sdate: `${newDate.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`
    };

    debouncedFn(() => {
      callExtensionAPI(newParams);
    });
  };

  // 대여 시간 변경 핸들러 예시
  const handleRentalTimeChange = (newTime) => {
    setRentalDateTime(prev => ({ ...prev, time: newTime }));
    
    // 시간 변경 시 API 호출
    const newParams = {
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${newTime?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      additional_sale: requestParams.additional_sale || "",
      point: requestParams.point || "",
      allPointUse: requestParams.allPointUse || "N",
      download_seq: requestParams.download_seq || "",
      deposit_price: requestParams.deposit_price || "",
      allDepositUse: requestParams.allDepositUse || "N",
      balance: requestParams.balance || "",
      allBalanceUse: requestParams.allBalanceUse || "N",
      maxCouponUse: requestParams.maxCouponUse || "N",
      undecidedChk: requestParams.undecidedChk || "N"
    };

    debouncedFn(() => {
      callExtensionAPI(newParams);
    });
  };

  // 반납 날짜 변경 핸들러 예시
  const handleReturnDateChange = (newDate) => {
    setReturnDateTime(prev => ({ ...prev, date: newDate }));
    calculateRentalDays(rentalDateTime.date, newDate);

    // 전체 필요 파라미터 포함
    const apiParams = {
      order_kind: "visit",
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${newDate.format('YYYY-MM-DD')} ${returnDateTime.time?.replace('시', ':00') || '00:00'}`,
      point: requestParams.point || "",
      allPointUse: requestParams.allPointUse || "N",
      balance: requestParams.balance || "",
      allBalanceUse: requestParams.allBalanceUse || "N",
      deposit_cash_num: requestParams.deposit_cash_num || "",
      deposit_cash_receipt: requestParams.deposit_cash_receipt || "N",
      download_seq: requestParams.download_seq || "",
      maxCouponUse: requestParams.maxCouponUse || "N",
      pay_select_goods: selectedProductIdsRef.current
    };

    debouncedFn(() => {
      instance.post(MY_PAGE_EXTENSION_URL, apiParams)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList?.bundleMain || [],
              bundleAdd: data.cartList?.bundleAdd || [],
              bundleAcc: data.cartList?.bundleAcc || []
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('API 호출 실패:', error);
        });
    });
  };

  // 반납 시간 변경 핸들러 수정
  const handleReturnTimeChange = (newTime) => {
    // 같은 날짜를 선택한 경우, 기존 시간보다 이후 시간만 선택 가능
    if (returnDateTime.date.isSame(rentalDateTime.date, 'day')) {
      const rentalTimeHour = parseInt(rentalDateTime.time.replace('시', ''), 10);
      const newTimeHour = parseInt(newTime.replace('시', ''), 10);
      
      if (newTimeHour <= rentalTimeHour) {
        // 경고 표시
        setIsPopupLoginError(t('return_time_selection2'));
        return;
      }
    }
    
    setReturnDateTime(prev => ({ ...prev, time: newTime }));
    
    // API 호출을 위한 파라미터 준비
    const apiParams = {
      order_kind: "visit",
      origin_order_seq: orderSeq.current,
      rental_store_seq: rentalState.selectValue || "",
      return_store_seq: returnState.selectValue || "",
      rental_sdate: `${rentalDateTime.date.format('YYYY-MM-DD')} ${rentalDateTime.time?.replace('시', ':00') || '00:00'}`,
      rental_edate: `${returnDateTime.date.format('YYYY-MM-DD')} ${newTime?.replace('시', ':00') || '00:00'}`,
      point: requestParams.point || "",
      allPointUse: requestParams.allPointUse || "N",
      balance: requestParams.balance || "",
      allBalanceUse: requestParams.allBalanceUse || "N",
      deposit_cash_num: requestParams.deposit_cash_num || "",
      deposit_cash_receipt: requestParams.deposit_cash_receipt || "N",
      download_seq: requestParams.download_seq || "",
      maxCouponUse: requestParams.maxCouponUse || "N",
      pay_select_goods: selectedProductIdsRef.current
    };

    // API 호출 (디바운스 적용)
    debouncedFn(() => {
      instance.post(MY_PAGE_EXTENSION_URL, apiParams)
        .then(response => {
          if (response.data.code === 200) {
            const data = response.data.data;
            setCartListData({
              bundleMain: data.cartList?.bundleMain || [],
              bundleAdd: data.cartList?.bundleAdd || [],
              bundleAcc: data.cartList?.bundleAcc || []
            });
            setOrderSummary(data);
          }
        })
        .catch(error => {
          console.error('API 호출 실패:', error);
        });
    });
  };

  //요청사항
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  const handleAllCheck = () => {
    setIsAllChecked(!isAllChecked);
    const newCheckedState = {
      option1: !isAllChecked,
      option2: !isAllChecked,
      option3: !isAllChecked,
      option4: !isAllChecked,
    };
    setIsChecked(newCheckedState);
  };

  const handleCheck = (option) => {
    const newCheckedState = { ...isChecked, [option]: !isChecked[option] };
    setIsChecked(newCheckedState);
    setIsAllChecked(Object.values(newCheckedState).every(Boolean));
  };

  // 초기화
  const handleReset = () => {
    window.location.reload();
  };

  // StoreOperationTime 컴포넌트 정의
  const StoreOperationTime = ({ storeList }) => {
    return (
      <div className="operation_time">
        {storeList.map((store) => (
          <p key={store.store_seq}>
            {store.store_name}
            <span>
              {store.opening_hour_set === 'Y' 
                ? `${formatTime(store.opening_start_hour, store.opening_start_min)}~${formatTime(store.opening_end_hour, store.opening_end_min)}`
                : t('24_hour_operation')}
            </span>
          </p>
        ))}
      </div>
    );
  };

  // 컴포넌트 마운트 시 초기 대여일수 계산
  useEffect(() => {
    calculateRentalDays(rentalDateTime.date, returnDateTime.date);
  }, [rentalDateTime.date, returnDateTime.date, calculateRentalDays]);

  // 드롭다운 열기/닫기 핸들러 추가
  const handleRentalDropdown = () => {
    setRentalState(prev => ({
      ...prev,
      isOpen: !prev.isOpen
    }));
  };

  const handleReturnDropdown = () => {
    setReturnState(prev => ({
      ...prev,
      isOpen: !prev.isOpen
    }));
  };

  const handleorderDetail = (goods_seq) => {
    if (true) {
      // 70% 새창으로 열기
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const windowWidth = Math.floor(screenWidth * 0.7);
      const windowHeight = Math.floor(screenHeight * 0.7);
      const left = Math.floor((screenWidth - windowWidth) / 2);
      const top = Math.floor((screenHeight - windowHeight) / 2);
      
      window.open(
        `/product/detail/${goods_seq}`,
        '_blank',
        `width=${windowWidth},height=${windowHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    }
  }

  // 재고 부족 항목이 있는 패키지 자동 펼치기
useEffect(() => {
  if (cartListData && (cartListData.bundleMain || cartListData.bundleAdd || cartListData.bundleAcc)) {
    const newIsShow = {};
    
    // bundleMain에서 bundleCp와 bundlePkg를 확인
    if (cartListData.bundleMain && cartListData.bundleMain.length > 0) {
      cartListData.bundleMain.forEach(item => {
        let hasStockShortage = false;
        
        // bundleCp 중에서 stock_flag가 'N'인 항목이 있는지 확인
        if (item.bundleCp && item.bundleCp.length > 0) {
          hasStockShortage = item.bundleCp.some(cpItem => cpItem.stock_flag === 'N');
        }
        
        // bundlePkg 중에서 stock_flag가 'N'인 항목이 있는지 확인
        if (!hasStockShortage && item.bundlePkg && item.bundlePkg.length > 0) {
          hasStockShortage = item.bundlePkg.some(pkgItem => pkgItem.stock_flag === 'N');
        }
        
        if (hasStockShortage) {
          newIsShow[item.cart_option_seq] = true; // 재고 부족 항목이 있으면 펼치기
        }
      });
    }
    
    // 기존 상태와 병합 (기존에 수동으로 열린 항목들은 유지)
    setIsShow(prev => ({
      ...prev,
      ...newIsShow
    }));
  }
}, [cartListData]);


  return(
    <div className="order_page extension_page">

      <div className="order_inner">
        {/* 매장방무주문 연장신청 */}
        {type === 'visit' && (
          <div className="o_rental_time">
            <div className="order_title">
              <h3>{t('return_point_and_time_selection')}</h3>
              <button className="reset_text_btn" onClick={handleReset}>
                {t('initialization')}
              </button>
            </div>
            
              <div className="rental_box"> 
                {/* 대여 */}
                <div className="rental_select">
                    <div className="rental_label">{t('existing_return_point_and_time')}</div>
                    <div className="select_info disabled_field">
                      {/* 대여 지점 */}
                      <div
                        ref={rentalSelectRef}
                        className={`default_select border ${rentalState.isOpen ? 'open' : ''}`}
                      >
                        <button
                          type="button"
                          className={`select_btn ${rentalState.selectValue ? 'active' : ''}`}
                          onClick={handleRentalDropdown}
                          disabled={true}
                        >
                          <span className="ellipsis_clamp">
                            {rentalState.selectItem ? ( 
                              <span className="select_store">{rentalState.selectItem}</span>
                              ) : (
                              <span>{t('rental_point_selection')}</span>
                              )}
                          </span>
                        </button>
                        <ul className="select_list">
                          {storeList.map((store) => (
                            <li
                              key={store.store_seq}
                              className={`select_item ellipsis1 ${
                                rentalState.selectValue === store.store_seq ? 'active' : ''
                              }`}
                              onClick={() => handleRentalSelectItem(store)}
                            >
                              {store.store_name}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* 대여 일시 */}
                      <DatePicker
                        value={rentalDateTime.date}
                        onDateChange={handleRentalDateChange}
                        maxDate={returnDateTime.date}
                        isDisabled={true}
                      />
                      <TimePicker
                        value={rentalDateTime.time}
                        onTimeChange={handleRentalTimeChange}
                        isDisabled={true}
                        storeInfo={rentalState.storeInfo}
                        compareDate={rentalDateTime.date}
                        isTodayCheck={true}
                      />
                    </div>

                </div>

                <span className="o_period">~</span>
                
                {/* 반납 */}
                <div className="return_select">
                  <div className="select_box">
                    <p className="rental_label">{t('modify_return_point_and_time')}</p>
                    <div className="select_info">
                      {/* 반납 지점 선택 */}
                      <div
                        ref={returnSelectRef}
                        className={`default_select border ${returnState.isOpen ? 'open' : ''}`}
                      >
                        <button
                          className={`select_btn ${returnState.selectValue ? 'active' : ''}`}
                          type="button"
                          onClick={handleReturnDropdown}
                        >
                          <span className="ellipsis_clamp">
                            {returnState.selectItem ? (
                              <span className="select_store">{returnState.selectItem}</span>
                            ) : (
                              <span>{t('select_return_branch')}</span>
                            )}
                          </span>
                        </button>
                        <ul className="select_list">
                          {storeList.map((store) => (
                            <li
                              key={store.store_seq}
                              className={`select_item ellipsis1 ${
                                returnState.selectValue === store.store_seq ? 'active' : ''
                              }`}
                              onClick={() => handleReturnSelectItem(store)}
                            >
                              {store.store_name}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* 반납 일시 */}
                        <DatePicker
                          value={returnDateTime.date}
                          onDateChange={handleReturnDateChange}
                          minDate={minSelectableDate}
                          isDisabled={false}
                        />
                        <TimePicker
                          value={returnDateTime.time}
                          onTimeChange={handleReturnTimeChange}
                          isDisabled={!returnState.storeInfo}
                          storeInfo={returnState.storeInfo}
                          compareDate={returnDateTime.date}
                          compareTime={rentalDateTime.time}
                          isReturn={true}
                          isTodayCheck={true}
                          minTime={returnDateTime.date.isSame(rentalDateTime.date, 'day') ? rentalDateTime.time : undefined}
                          rentalDateTime={rentalDateTime}
                        />
                    </div>
                  </div>
                </div>
              </div>

              {/*  지점별 영업시간 > 관리자에서 표출 설정 */}
              <StoreOperationTime storeList={storeList} />
              
              {/*  총 대여일 */}
              <div 
                className="total_rental flex-center-2"
                style={{marginTop:"0"}}
              >
                <span>{t('total_rental_days2')}</span>
                <p><span className="strong_b1">{orderSummary?.totalInfo?.rental_period}</span>{t('total_rental_days_count_0417')}</p>
              </div>
          </div>
        )}

        {type === 'parcel' && (
          <div className="o_rental_time">
            <div className="order_title">
              <h3>{t('rental_end_date_selection')}</h3>
              <button className="reset_text_btn" onClick={handleReset}>
                {t('initialization')}
              </button>
            </div>
            
            <div className="rental_box"> 
              {/* 대여 시작 */}
              <div className="rental_select">
                <div className="rental_label">{t('existing_rental_end_date')}</div>
                <div className="select_info">
                  <DatePicker
                    value={rentalDateTime.date}
                    onDateChange={handleRentalDateChange}
                    isDisabled={true}
                  />
                </div>
              </div>
              <span className="o_period">~</span>
              {/* 대여 종료 */}
              <div className="return_select">
                <div className="select_box">
                  <p className="rental_label">{t('receive_rental_end_date')}</p>
                  <div className="select_info">
                    <DatePicker
                      value={returnDateTime.date}
                      onDateChange={handleReturnDateChange}
                      minDate={rentalDateTime.date}
                      isDisabled={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/*  총 대여일 - 계산된 값으로 업데이트 */}
            <div 
              className="total_rental flex-center-2"
              style={{marginTop:"0"}}
            >
              <span>{t('total_rental_days2')}</span>
              <p><span className="strong_b1">{totalRentalDays}</span>{t('total_rental_days_count_0417')}</p>
            </div>
          </div>

        )}

        {/* 공통 */}
        {/* 상품 정보 UI들은 견적서 페이지와 똑같습니다. 다만, 상품 총 수량, 합계만 없습니다. */}
        {/* 대여상품 정보 */}
        {cartListData?.bundleMain && cartListData.bundleMain.length > 0 && (
        <section className="mt_48">
          <div className="order_title">
            <h3>{t('rental_product_info')}</h3>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData?.bundleMain && cartListData.bundleMain.length > 0 ? (
                cartListData.bundleMain.map((item, index) => (
                  <li key={item.cart_option_seq || index} onClick={() => handleorderDetail(item.goods_seq)}>
                    <div className="order_item default_prod">
                      <div className="prod_container">
                        <img
                          src={item.goods_image || product}
                          alt="상품 이미지"
                          className="order_prod_img"
                          loading="lazy"
                        />
                        <div className="prod_info">
                          <div className="info_box">
                            <div className="prod_title">
                              <p className="title ellipsis1">{item.product_name || '상품명'}</p>
                              <p className="sub_title ellipsis1">{item.product_name02 || '부제목'}</p>
                              <p className="option_count">패키지: {item.package_items?.join(', ') || '기본 패키지'} / 수량<span className="quo_count"> :{item.ea || 1}개</span></p>
                              <p className="strong_b3" style={{marginTop:'8px'}}>₩<span className="strong_b1">{formatLocalString(item.sum_sale_price || 0)}</span></p>
                            </div>
                          </div>
                        </div>
                      </div>
                  </div>

                    {/* bundlePkg 데이터가 있을 경우 표시 */}
                    {item.bundlePkg && item.bundlePkg.length > 0 && (
                      <div className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                          onClick={() => handleShow(item.cart_option_seq)}>
                        <div className="package_total flex-justify-center">
                          <div className="flex-center-2">
                            {t('package')}<span className="strong_ch">{item.bundlePkg.length}</span>개<i className="arrow_icon"></i>
                          </div>
                        </div>
                        {item.bundlePkg.map((pkgItem, pkgIndex) => (
                          <div key={pkgItem.cart_option_seq || pkgIndex} className="package_item">
                    <div className="prod_container">
                      <img
                        src={pkgItem.goods_image || product}
                        alt="상품 이미지"
                        className="order_prod_img"
                        loading="lazy"
                      />
                      <div className="prod_info">
                        <div className="info_box">
                          <div className="prod_title">
                              <p className="title ellipsis1">{pkgItem.product_name || '패키지 상품명'}</p>
                              <p className="sub_title ellipsis1">{pkgItem.description_kr_01 || '패키지 부제목'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>
                    )}

                    {/* bundleCp가 있는 경우에만 구성품 내용물 표시 */}
                    {item.bundleCp && item.bundleCp.length > 0 && (
                      <div className={`package_con ${isShow[item.cart_option_seq] ? 'active' : ''}`}
                          onClick={() => handleShow(item.cart_option_seq)}>
                        <div className="package_total flex-justify-center">
                          <div className="flex-center-2">
                            구성품<span className="strong_ch">{item.bundleCp.length}</span>개<i className="arrow_icon"></i>
                          </div>
                        </div>
                        {item.bundleCp.map((pkgItem, pkgIndex) => (
                          <div key={pkgItem.cart_option_seq || pkgIndex} className="package_item">
                    <div className="prod_container">
                      <img
                        src={pkgItem.goods_image || product}
                        alt="상품 이미지"
                        className="order_prod_img"
                        loading="lazy"
                      />
                      <div className="prod_info">
                        <div className="info_box">
                          <div className="prod_title">
                              <p className="title ellipsis1">{pkgItem.product_name || '패키지 상품명'}</p>
                              <p className="sub_title ellipsis1">{pkgItem.description_kr_01 || '패키지 부제목'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))
              ) : (
                <li>
                  <div className="order_item default_prod">
                    <div className="prod_container">
                      <img
                        src={product}
                        alt="상품 이미지"
                        className="order_prod_img"
                        loading="lazy"
                      />
                      <div className="prod_info">
                        <div className="info_box">
                          <div className="prod_title">
                            <p className="title ellipsis1">ARRI ALEXA Mini Full</p>
                            <p className="sub_title ellipsis1">PL Mountd PL Mountd PL Mountd</p>
                            <p className="option_count">패키지: ARRI ALEXA Mini, PL Mountd, PL Mountd,ARRI ALEXA Mini, PL Mountd, ARRI ALEXA Mini, PL Mountd,ARRI ALEXA Mini / 수량<span className="quo_count"> :1개</span></p>
                            <p className="strong_b3" style={{marginTop:'8px'}}>₩<span className="strong_b1"></span>200,000</p>
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
              </li>
              )}
            </ul>
          </div>
        </section>
        )}

        {/* 무상품목 정보 */}
        {cartListData.bundleAcc && cartListData.bundleAcc.length > 0 && (
        <section className="mt_48">
          <div className="order_title">
            <h3>{t('acc_info_txt')}</h3>
          </div>
          <div>
            <ul className="rent order_prod_list">
              {cartListData.bundleAcc && cartListData.bundleAcc.map((item, index) => (
                <li key={`acc-${index}`}>
                <div className="order_item default_prod">
                  <div className="prod_container">
                    <img
                        src={item.goods_image || product}
                        alt="상품 이미지"
                        className="order_prod_img"
                        loading="lazy"
                    />
                    <div className="prod_info">
                      <div className="info_box">
                        <div className="prod_title">
                            <p className="title ellipsis1">{item.product_name}</p>
                            {item.price > 0 && (
                              <p className="strong_b3" style={{marginTop:'8px'}}>
                                ₩<span className="strong_b1">{formatLocalString(item.price)}</span>
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              ))}
            </ul>
          </div>
        </section> 
        )}

        {/* 결제금액 */}
        <section className="mt_48">
          <div className="order_title">
            <h3>{t('payment_amount')}</h3>
          </div>
          <div>
            <OrderPayment
              orderSummary={orderSummary}
              onUpdateParams={handleUpdateParams}
              orderType={type}
              orderStatus="extension"
            />
            
          </div>
        </section>

        {/* 요청사항 */}
        <section className="mt_48 container request_input">
          <div className="order_title">
            <h3>{t('request_txt')}</h3>
            <p className="text_small_b">{t('need_help_0617')}
              <span className="strong_b3 black">02-1544-2338</span>
              {t('need_help_0617_2')}
            </p>
          </div>
          <div className="default_input">
            <textarea 
              className="form_input"
              value={memo}
              onChange={handleMemoChange}
            />
          </div>
        </section> 
      </div>

      
      {/* 결제정보표시 > 공통 ; 단, 방문, 택배 에서 '반납지점' 표출 유무 다릅니다. */}
      {/* 기존 VisitOrder.js의 결제정보표시와 텍스트 조금 다릅니다. > 다른 부분 주석 남겼습니다.*/}
      <div className="total_payment_con">
        <div className="order_info_box">
          <div className="order_left">
            <div className="top_con flex-8 flex-start">
              <i className="p_icon">P</i>
              <p className="point_info">
                <span>
                  {t('normal_rental_and_return_txt')} {orderSummary?.pointSetting?.payStar1}%(₩{orderSummary?.pointSetting?.payStarMin ? formatLocalString(orderSummary?.pointSetting?.payStarMin) : '0'}) ~ {orderSummary?.pointSetting?.payStar5}%(₩{orderSummary?.pointSetting?.payStarMax ? formatLocalString(orderSummary?.pointSetting?.payStarMax) : '0'}) {t('point_txt')}
                </span>
                <button
                  className="text_small g-80"
                  type="button"
                  onClick={handleEquipmentPopup}
                >
                  {t('equipment_rating_info')}
                </button>
              </p>
            </div>
            <div className="bottom_con">
              <div className="join_con">
                <div className="default_chk_box all_chk">
                  <input 
                    type="checkbox" 
                    id="allChk" 
                    checked={isAllChecked}
                    onChange={handleAllCheck}
                  />
                  <label htmlFor="allChk">
                    <span>{t('order_info_check_0617')}</span>
                  </label>
                </div>  
                
                <ul className="flex-2 column g-90">
                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree1"  
                      name="option1"
                      checked={isChecked.option1}
                      onChange={() => handleCheck('option1')}
                    />
                    <label htmlFor="useAgree1">
                      <span className="flex-btw">
                        {t('order_product_agree_0617')}
                        <a href="/about/terms" className="g-70">{t('view')}</a>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox"
                      id="useAgree2"  
                      name="option2"
                      checked={isChecked.option2}
                      onChange={() => handleCheck('option2')}
                    />
                    <label htmlFor="useAgree2">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617')}
                        <a href="/about/terms" className="g-70">{t('view')}</a>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="personalAgree3" 
                      name="option3"
                      checked={isChecked.option3}
                      onChange={() => handleCheck('option3')}
                    />
                    <label htmlFor="personalAgree3">
                      <span className="flex-btw">
                        {t('order_personal_info_agree_0617_2')}
                        <a href="/personal-agreement" className="g-70">{t('view')}</a>
                      </span>
                    </label>
                  </li>

                  <li className="default_chk_box">
                    <input 
                      type="checkbox" 
                      id="personalAgree4" 
                      name="option4"
                      checked={isChecked.option4}
                      onChange={() => handleCheck('option4')}
                    />
                    <label htmlFor="personalAgree4">
                      <span className="flex-btw">
                        {t('order_payment_service_agree_0617')}
                        <a href="/personal-agreement" className="g-70">{t('view')}</a>
                      </span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="order_right">
            <div className="total_detail">
              <div className="total_count">
                <ul className="flex-2 column">

                  {/* 방문 연장: '반납지점' 표출 
                  / 택배 연장 : '반납지점' 미표출  */}
                  <li className="flex-btw"> 
                    {/* 주석 : 기존 VisitOrder.js */}
                    {/* 대여/반납 지점 */}
                    {t('return_point_txt')}
                    <p>{returnState.selectItem}</p>
                  </li>
                  <li className="flex-btw"> 
                    {t('total_product_quantity_txt')}
                    <p>{orderSummary?.totalInfo?.total_ea || 0}<span className="text_small">개</span></p>
                  </li>
                  <li className="flex-btw g-70">
                    &#8735;{t('rental_product4')}
                    <p>{orderSummary?.totalInfo?.total_rental_ea || 0}<span className="cap2_1">개</span></p>
                  </li>                    
                  <li className="flex-btw g-70">
                    &#8735;{t('additional_items_accessories_0617')}
                    <p>{orderSummary?.totalInfo?.total_etc_ea || 0}<span className="cap2_1">개</span></p>
                  </li>
                </ul>         
              </div>
              <div className="total_day">
                <div className="flex-btw">
                  {/* 총 대여일 */}
                  {t('total_extension_rental_days_txt')}
                  <p className="text_small">
                    ({rentalDateTime.date.format('YYYY.MM.DD')}~{returnDateTime.date.format('YYYY.MM.DD')})
                    <span className="strong_b1">{totalRentalDays}</span>{t('days_txt')}
                  </p>
                </div>
              </div>
            </div>
            <div className="total_amount"> 
              <div className="flex-btw price_box">
                <p className="label red">{t('total_payment_amount_0617')}:</p>
                <p className="o_total_price red">
                  ₩<span>{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}</span>
                </p> 
              </div> 
              <p 
                className="text_small_b g-70" 
                style={{textAlign:'right'}}
              >
                ₩{formatLocalString(orderSummary?.totalInfo?.settlePrice || 0)}{t('total_payment_amount_0617_2')}
              </p>
            </div>

            <div className="payment_btn">
              <button
                type="button"
                className="cancel_btn"
                onClick={()=> navigate('/order/quotationForm')}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                className="pay_black_btn"
                onClick={handleExtension}
              >
                {t('extension_application')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PC 결제창 모달 */}
      {isPayment && (
        <div id="payment" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 102
        }}>
          <div className="dimmed" style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.50)',
            zIndex: 101
          }} />
          <iframe
            id="payment-container"
            name="paymentIframe"
            title="이니시스 결제창"
            style={{
              width: '820px',
              height: '600px',
              position: 'relative',
              zIndex: 1000,
              border: 'none'
            }}
          />
        </div>
      )}

      <EquipmentRatingPopup 
        isPopup={isEquipmentPopup} 
        setIsPopup={setEquipmentPopup} 
      />

      <PopupLoginError
        isPopup={isPopupLoginError}
        setIsPopup={setIsPopupLoginError}
      />

    </div>
  )
};

export default OrderExtension;